/**
 * Who's on a job day. A time block may carry `crew: [uid]` — the people scheduled onto it (web Week → Team). No crew
 * = the block belongs to whoever made it (`ownerUid`), as every block did before crews. Your own plan (dayplan) only
 * counts blocks you're on, so scheduling someone else onto a job doesn't pull it into your day. Pure.
 */
import { blockMinutes } from './commands/blocks.js';

export const blockCrew = (b) => (Array.isArray(b?.crew) && b.crew.length ? b.crew : null);

/** Is `uid` on this block? `fallbackOwner` stands in for a block with neither crew nor ownerUid (made on this device). */
export function blockOn(b, uid, fallbackOwner = uid) {
  const crew = blockCrew(b);
  if (crew) return crew.includes(uid);
  return (b?.ownerUid || fallbackOwner) === uid;
}

/**
 * The team's week as a table: one row per person, one cell per day — the job days they're on (blocks), the open
 * tasks assigned to them that day, and the booked minutes. people: [{ uid, name, ... }] (me first if present).
 * Tasks count for a person when `assignee` is them, or unassigned and theirs (`ownerUid`, or mine with no owner).
 */
export function crewWeek({ people = [], blocks = [], tasks = [], days = [], me = null }) {
  const inWeek = new Set(days);
  return people.map((person) => {
    const uid = person.uid;
    const cells = days.map((date) => ({ date, jobs: [], tasks: 0, minutes: 0 }));
    const at = new Map(cells.map((c) => [c.date, c]));
    for (const b of blocks) {
      if (!inWeek.has(b.date) || !b.projectId || !blockOn(b, uid, me)) continue;
      const [s, e] = blockMinutes(b);
      const c = at.get(b.date);
      c.jobs.push({ blockId: b.id, projectId: b.projectId, start: s, end: e, crew: blockCrew(b) });
      c.minutes += Math.max(0, e - s);
    }
    for (const t of tasks) {
      if (!inWeek.has(t.scheduledDate) || t.status === 'done' || t.bucket) continue;
      const mine = t.assignee ? t.assignee === uid : (t.ownerUid || me) === uid;
      if (mine) at.get(t.scheduledDate).tasks++;
    }
    for (const c of cells) c.jobs.sort((a, b) => a.start - b.start);
    return {
      uid, person, cells,
      minutes: cells.reduce((a, c) => a + c.minutes, 0),
      daysOn: cells.filter((c) => c.jobs.length).length,
    };
  });
}

/**
 * Writes-free plan for "put `uid` on `projectId` on `date`": join an existing block for that job that day (add to its
 * crew) or make a new job day. With `exact`, only a block with the same hours counts as that job day. Returns { join: { id, crew } } or { add: { projectId, date, kind, start, end, crew } }.
 */
export function scheduleCrew({ blocks = [], uid, projectId, date, me = null, start = 8 * 60, end = 16 * 60, exact = false }) {
  // exact = the hours were picked: only a block with those same hours is the same job day (else a new block).
  const same = blocks.filter((b) => b.projectId === projectId && b.date === date
    && (!exact || (([s, e]) => s === start && e === end)(blockMinutes(b))));
  if (same.some((b) => blockOn(b, uid, me))) return { already: true };
  const joinable = same.find((b) => blockCrew(b)) || same[0];
  if (joinable) {
    const crew = blockCrew(joinable) || [joinable.ownerUid || me].filter(Boolean);
    return { join: { id: joinable.id, crew: [...new Set([...crew, uid])] } };
  }
  return { add: { projectId, date, kind: 'job-day', start, end, crew: [uid] } };
}

/** Take `uid` off a block: { update: { id, crew } }, or { remove: id } when nobody is left on it. */
export function unscheduleCrew(block, uid, me = null) {
  const crew = blockCrew(block) || [block.ownerUid || me].filter(Boolean);
  const left = crew.filter((u) => u !== uid);
  return left.length ? { update: { id: block.id, crew: left } } : { remove: block.id };
}
